<script setup lang="ts">
import { computed, ref } from 'vue'
import { isUndefined, range } from 'lodash'

import ViewLayout from '@/views/layouts/ViewLayout.vue'

import useProgramEarningsService, { MISOProgram } from '@/composables/eos/program-earnings-dashboard'

import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import RcSiteSelect from '@/components/rc/common/RcSiteSelect.vue'

import ProgramEarningsPieChart from '@/components/eos/charts/ProgramEarningsPieChart.vue'
import ProgramEarningsStackChart from '@/components/eos/charts/ProgramEarningsStackChart.vue'
import EarningsDashboardTable from '@/components/eos/demand-response/EarningsDashboardTable.vue'
import DashboardSummaryCard from '@/components/eos/demand-response/EarningsDashboardSummaryCard.vue'
import { DateTime } from 'luxon'

const siteSelected = ref<ISiteIdentifier>()
const yearSelected = ref(DateTime.now().year)
const monthSelected = ref<number>()
const programSelected = ref<MISOProgram>()

const compareToPreviousYear = ref(false)

const activeTab = ref<MISOProgram>(MISOProgram.All)

const availablePrograms = computed(() => Object.values(MISOProgram).filter((value) => value !== MISOProgram.All))

const years = range(2020, DateTime.now().year + 1).reverse()

const { yearEarningsSummaryQuery } = useProgramEarningsService()

const {
  data: yearData,
  error: yearError,
  isLoading: yearIsLoading,
  isFetching: yearIsFetching,
} = yearEarningsSummaryQuery(siteSelected, yearSelected)

const {
  data: previousYearData,
  error: previousYearError,
  isLoading: previousYearIsLoading,
  isFetching: previousYearIsFetching,
} = yearEarningsSummaryQuery(
  siteSelected,
  computed(() => yearSelected.value - 1),
  compareToPreviousYear // Works as 'enabled' flag
)

const aggregateError = computed(() => yearError.value ?? previousYearError.value)

const anyFetching = computed(() => yearIsFetching.value || previousYearIsFetching.value)

function refetchFailed() {
  // Reset state -- Will disable queries
  siteSelected.value = undefined
  yearSelected.value = DateTime.now().year
}

const showTable = computed(
  () =>
    !!siteSelected.value &&
    !!yearSelected.value &&
    !isUndefined(monthSelected.value) &&
    activeTab.value !== MISOProgram.All
)
</script>

<template>
  <ViewLayout>
    <!-- Year query Error state -->
    <rl-error-result v-if="aggregateError && !yearIsFetching" :error="aggregateError">
      <el-button class="mt-4" @click="refetchFailed()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>

    <template v-else>
      <!-- Top Site + Year pickers -->
      <div class="flex justify-between">
        <rc-site-select v-model="siteSelected" class="w-45" :disabled="anyFetching" />
        <el-select v-model="yearSelected" class="w-30" label="Year" :disabled="anyFetching">
          <el-option v-for="yr in years" :key="yr" :value="yr">
            {{ yr }}
          </el-option>
        </el-select>
      </div>

      <!-- Stack and pie charts -->
      <div class="flex gap-4">
        <el-card class="w-full h-80">
          <!-- Empty -->
          <div
            v-if="isUndefined(siteSelected) || isUndefined(yearSelected)"
            class="h-full h-full flex items-center justify-center"
          >
            <span class="text-gray-500">Please select a site and year.</span>
          </div>

          <!-- Loading -->
          <div v-else-if="yearIsLoading" class="h-full flex items-center justify-center">
            <span class="text-gray-500">Loading...</span>
          </div>

          <!-- Stack chart -->
          <div v-else class="h-full flex flex-col">
            <div class="flex items-center gap-6 pb-4 shrink-0">
              <span class="uppercase text-xs">Total Earnings by Program</span>

              <el-switch
                v-model="compareToPreviousYear"
                v-loading="previousYearIsLoading"
                active-text="Compare to previous year"
                class="ml-auto"
              />

              <el-select v-model="programSelected" class="w-50" clearable placeholder="All Programs">
                <el-option v-for="program in availablePrograms" :key="program" :value="program">{{
                  program
                }}</el-option>
              </el-select>
            </div>

            <div class="flex-1 min-h-0">
              <ProgramEarningsStackChart
                :data="yearData"
                :previous-year-data="compareToPreviousYear ? previousYearData : undefined"
                :program-filter="programSelected"
                :selected-month="monthSelected"
                :selected-program="activeTab"
                :selected-year="yearSelected"
                @select="
                  ({ year, month, program }) => {
                    if (yearSelected === year) {
                      monthSelected = month
                      activeTab = program
                    }
                  }
                "
              />
            </div>
          </div>
        </el-card>
      </div>
    </template>

    <template v-if="yearData?.length">
      <!-- Tabs -->
      <el-tabs v-model="activeTab">
        <el-tab-pane label="All Programs" :name="MISOProgram.All" />
        <el-tab-pane label="Day Ahead Price Response" :name="MISOProgram.DAPR" />
        <el-tab-pane label="Operating Reserves" :name="MISOProgram.OR" />
      </el-tabs>

      <!-- Summary card -- manages monthSelected and activeTab -->
      <DashboardSummaryCard
        v-if="activeTab !== MISOProgram.All"
        v-model:month-selected="monthSelected"
        v-model:active-tab="activeTab"
        :site-selected="siteSelected"
        :year-selected="yearSelected"
      />

      <el-card v-else class="w-full h-64">
        <!-- Empty -->
        <div
          v-if="isUndefined(siteSelected) || isUndefined(yearSelected)"
          class="h-full h-full flex items-center justify-center"
        >
          <span class="text-gray-500">Please select a site and year.</span>
        </div>

        <!-- Loading -->
        <div v-else-if="yearIsLoading" class="h-full flex items-center justify-center">
          <span class="text-gray-500">Loading...</span>
        </div>

        <!-- Pie chart -->
        <div v-else class="h-full flex flex-col gap-4">
          <span class="uppercase text-xs text-text-secondary">Total Earnings by Program</span>
          <ProgramEarningsPieChart :data="yearData" @select="({ program }) => (activeTab = program)" />
        </div>
      </el-card>
    </template>

    <!-- Note: siteSelected and monthSelected are defined if showTable is true -->
    <EarningsDashboardTable
      v-if="showTable"
      :site-selected="siteSelected!"
      :year-selected="yearSelected"
      :month-selected="monthSelected!"
      :program-selected="activeTab"
    />
  </ViewLayout>
</template>

<style scoped lang="scss">
.el-card :deep(.el-card__body) {
  height: 100%;
  box-sizing: border-box;
}

:deep() {
  .el-switch {
    &__label {
      color: var(--text-secondary);
    }
  }
}
</style>
